import { useTranslate } from '@ur-solutions/react-hooks'
import type {
  CheckedTableRow,
  CheckedTableValue,
} from '@/components_deprecated'
import { CheckedTable } from '@/components_deprecated'
import differenceWith from 'lodash/differenceWith'
import isEqual from 'lodash/isEqual'
import omit from 'lodash/omit'
import React, {
  useCallback,
  useEffect,
  useEffectEvent,
  useMemo,
  useRef,
  useState,
} from 'react'
import ScrollBar from 'react-perfect-scrollbar'
import styled from 'styled-components'
import type {
  CompanyWithNotificationSettings,
  NotificationSettingsNames,
} from '../../util/NotificationSettings'
import { NotificationSettings } from '../../util/NotificationSettings'
import { useModuleConfigs } from '../../hooks'

const Wrapper = styled.div`
  position: relative;
  width: 100%;
  overflow-x: auto;
`

interface NotificationSettingsTableProps {
  company: CompanyWithNotificationSettings

  onChange: (diff: CheckedTableValue[]) => void
}

export const NotificationSettingsTable: React.FC<
  NotificationSettingsTableProps
> = ({
  company,

  onChange,
}) => {
  const translations = useTranslate({
    inAppNotification: 'common.in-app',
    emailNotification: 'common.email',
    smsNotification: 'common.sms',

    planner: 'common.planner',
    workInfringements: 'infringements.work-day-infringements',
    tachographInfringements: 'infringements.tachograph-infringements',
  })

  const regularSettingTranslations = useTranslate({
    userRegistration: 'settings.notifications.setting.user-registration',
    resetPassword: 'settings.notifications.setting.reset-password',
    userDocument: 'settings.notifications.setting.user-document',
    issueCreated: 'settings.notifications.setting.issue-created',
    issueClosed: 'settings.notifications.setting.issue-closed',
    absenceCreated: 'settings.notifications.setting.absence-created',
    absenceStatusChanged:
      'settings.notifications.setting.absence-status-changed',
    absenceRequestChanged:
      'settings.notifications.setting.absence-request-changed',
    forumThreadCreated: 'settings.notifications.setting.forum-thread',
    forumPostCreated: 'settings.notifications.setting.forum-post',
    driverCardWillExpire: 'settings.notifications.setting.driver-card',
    userExportReady: 'settings.notifications.setting.user-export-ready',
    vehicleExpiration: 'settings.notifications.vehicle-expirations',
    vehicleDocument: 'settings.notifications.vehicle-document',
    newInfringement: 'settings.notifications.new-infringement',
    infringementLeaderComment:
      'settings.notifications.infringement-leader-comment',
    infringementExplanationChanged:
      'settings.notifications.infringement-explanation-changed',
    infringementReminder: 'settings.notifications.infringement-reminder',
    issueReminder: 'settings.notifications.issue-reminder',
    workPeriodApproval: 'settings.notifications.work-period-approval',
    issueReopened: 'settings.notifications.issue-reopened',
    issueArchived: 'settings.notifications.issue-archived',
    issueUnarchived: 'settings.notifications.issue-unarchived',
  })

  const workInfringementSettingTranslations = useTranslate({
    newWorkInfringement: 'settings.notifications.new-work-infringement',
    workInfringementLeaderComment:
      'settings.notifications.work-infringement-leader-comment',
    workInfringementReceipted:
      'settings.notifications.work-infringement-receipted',
    workInfringementReminder:
      'settings.notifications.work-infringement-reminder',
  })
  const tachographInfringementSettingTranslations = useTranslate({
    newTachographInfringement:
      'settings.notifications.new-tachograph-infringement',
    tachographInfringementLeaderComment:
      'settings.notifications.tachograph-infringement-leader-comment',
    tachographInfringementReceipted:
      'settings.notifications.tachograph-infringement-receipted',
    tachographInfringementReminder:
      'settings.notifications.tachograph-infringement-reminder',
  })
  const { active: activeModules } = useModuleConfigs()

  const plannerSettingTranslations = useTranslate({
    plannerCalendarEventChange:
      'settings.notifications.planner-calendar-event-change',
  })

  const prevCompany = useRef<CompanyWithNotificationSettings | null>(null)
  const initialValues = useRef<CheckedTableValue[]>([])

  const [checkedCollection, setCheckedCollection] = useState<
    CheckedTableValue[]
  >([])

  const createCheckedValues = useCallback(
    (company: CompanyWithNotificationSettings) => {
      const cleaned = omit(company, 'id', '__typename').notificationSettings

      const columns = ['sendInApp', 'sendEmail', 'sendSms'] as const

      const values: CheckedTableValue[] = []
      for (const row of cleaned) {
        if (columns.includes('sendInApp')) {
          for (const column of columns) {
            values.push({
              column,
              row: row.id,
              checked: row[column],
            })
          }
        } else {
          const reducedColumns = ['sendEmail', 'sendSms'] as const
          values.push({
            column: 'sendInApp',
            row: row.id,
            checked: null,
          })
          for (const column of reducedColumns) {
            values.push({
              column,
              row: row.id,
              checked: row[column],
            })
          }
        }
      }
      return values
    },
    []
  )

  const handleSetCheckedCollection = useEffectEvent(
    (values: CheckedTableValue[]) => {
      setCheckedCollection(values)
    }
  )

  useEffect(() => {
    if (isEqual(prevCompany.current, company)) return
    prevCompany.current = company

    const values = createCheckedValues(company)
    initialValues.current = values
    handleSetCheckedCollection(values)
  }, [company, createCheckedValues])

  const columns = useMemo(
    () => [
      translations.inAppNotification,
      translations.emailNotification,
      translations.smsNotification,
    ],
    [
      translations.emailNotification,
      translations.inAppNotification,
      translations.smsNotification,
    ]
  )

  const getIdForTranslation = useCallback(
    (
      key: string,
      cleaned: { id: string; name: NotificationSettingsNames }[]
    ) => {
      const found = cleaned.filter(
        value => NotificationSettings[value.name] === key
      )?.[0]

      return found?.id ?? ''
    },
    []
  )

  const rows = useMemo<CheckedTableRow[]>(() => {
    const cleaned = omit(company, 'id', '__typename').notificationSettings.map(
      node => ({ id: node.id, name: node.name })
    )

    const rows: CheckedTableRow[] = [
      {
        type: 'header',
        title: 'TaCtrl',
        value: 'header-tactrl',
      },
    ]

    for (const [key, value] of Object.entries(regularSettingTranslations))
      rows.push({
        type: 'regular',
        title: value,
        value: getIdForTranslation(key, cleaned),
      })

    rows.push({
      type: 'header',
      title: translations.planner,
      value: 'header-planner',
    })

    for (const [key, value] of Object.entries(plannerSettingTranslations))
      rows.push({
        type: 'regular',
        title: value,
        value: getIdForTranslation(key, cleaned),
      })

    if (activeModules.workInfringements) {
      rows.push({
        type: 'header',
        title: translations.workInfringements,
        value: 'header-work-infringements',
      })

      for (const [key, value] of Object.entries(
        workInfringementSettingTranslations
      ))
        rows.push({
          type: 'regular',
          title: value,
          value: getIdForTranslation(key, cleaned),
        })
    }

    if (activeModules.infringements && activeModules.tachographInfringements) {
      rows.push({
        type: 'header',
        title: translations.tachographInfringements,
        value: 'header-tachograph-infringements',
      })

      for (const [key, value] of Object.entries(
        tachographInfringementSettingTranslations
      ))
        rows.push({
          type: 'regular',
          title: value,
          value: getIdForTranslation(key, cleaned),
        })
    }

    return rows
  }, [
    company,
    translations.planner,
    translations.workInfringements,
    regularSettingTranslations,
    getIdForTranslation,
    plannerSettingTranslations,
    workInfringementSettingTranslations,
    activeModules.workInfringements,
    translations.tachographInfringements,
    tachographInfringementSettingTranslations,
    activeModules.infringements,
    activeModules.tachographInfringements,
  ])

  function handleChange(values: CheckedTableValue[]) {
    const diff = differenceWith(values, initialValues.current, isEqual)
    setCheckedCollection(values)

    onChange(diff)
  }

  return (
    <Wrapper>
      <ScrollBar>
        <CheckedTable
          value={checkedCollection}
          columns={columns}
          rows={rows}
          onChange={handleChange}
        />
      </ScrollBar>
    </Wrapper>
  )
}
